
import React from "react";
import { formatCurrency } from '../../utils/calculator';
import { InfoTooltip } from '../../ui/InfoTooltip';
import { ShieldCheckIcon, EuroIcon } from 'lucide-react';

interface WeeklySummaryProps {
  actualWeeklyPayment: number;
  weeklyPaymentWithLoan: number;
  nursingHomeCost: number;
  deferredAmount: number;
  isUsingLoan: boolean;
  hasNursingHomeCost: boolean;
}

export function WeeklySummary({
  actualWeeklyPayment,
  weeklyPaymentWithLoan,
  nursingHomeCost,
  deferredAmount,
  isUsingLoan,
  hasNursingHomeCost
}: WeeklySummaryProps) {
  const isCapped = hasNursingHomeCost && weeklyPaymentWithLoan > nursingHomeCost;
  const stateSupport = hasNursingHomeCost ? Math.max(0, nursingHomeCost - actualWeeklyPayment) : 0;
  
  return (
    <div className="bg-white p-6 rounded-lg border shadow-sm">
      <h3 className="text-lg text-grandpal-green font-semibold mb-4">Your Fair Deal Breakdown</h3>
      
      <div className="space-y-6">
        {hasNursingHomeCost && (
          <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-gray-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <EuroIcon className="h-5 w-5 text-gray-700" />
                <p className="text-gray-700 font-medium">Nursing Home Cost</p>
              </div>
              <p className="text-xl font-bold text-gray-800">{formatCurrency(nursingHomeCost)}/week</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-gray-600">Your Means Assessment Result</p>
              <InfoTooltip content="This is what your calculated contribution would be based on your income and assets before any caps are applied." />
            </div>
            <p className={`text-lg font-medium ${isCapped ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
              {formatCurrency(weeklyPaymentWithLoan)}/week
            </p>
            {isCapped && (
              <p className="text-sm text-amber-600 mt-1">
                Your assessment exceeds the nursing home cost and is therefore capped.
              </p>
            )}
          </div>

          <div className="bg-green-50 p-5 rounded-lg border-l-4 border-green-500">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5 text-green-700" />
                <p className="text-green-800 font-semibold">What You Actually Pay</p>
              </div>
              <InfoTooltip content="This is your actual weekly payment after applying the Fair Deal rules." />
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-bold text-green-700">
                {formatCurrency(hasNursingHomeCost ? Math.min(weeklyPaymentWithLoan, nursingHomeCost) : weeklyPaymentWithLoan)}
              </p>
              <p className="text-sm text-green-600">/week</p>
            </div>
            
            {isCapped && (
              <div className="mt-2 py-1 px-2 bg-green-100 rounded text-sm text-green-700 inline-block">
                Capped at nursing home cost
              </div>
            )}
          </div>
          
          {hasNursingHomeCost && stateSupport > 0 && (
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="h-5 w-5 text-blue-700" />
                  <p className="text-blue-800 font-medium">Fair Deal State Support</p>
                </div>
                <p className="text-xl font-bold text-blue-700">{formatCurrency(stateSupport)}/week</p>
              </div>
            </div>
          )}
        </div>
        
        {isUsingLoan && (
          <div className="mt-4 border-t pt-4">
            <p className="flex items-center gap-1 text-sm text-amber-700">
              <span>+ {formatCurrency(deferredAmount)}/week deferred via Nursing Home Loan</span>
              <InfoTooltip content="This amount will be deferred via the Nursing Home Loan and recovered from your estate later." />
            </p>
          </div>
        )}

        {isCapped && (
          <div className="mt-4 bg-blue-50 border border-blue-100 rounded-md p-4">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Important:</span> Your means assessment ({formatCurrency(weeklyPaymentWithLoan)}/week) 
              exceeds the nursing home cost. Under Fair Deal rules, you'll only pay the actual nursing home 
              cost of {formatCurrency(nursingHomeCost)}/week, saving you {formatCurrency(weeklyPaymentWithLoan - nursingHomeCost)}/week.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
